import { createFileRoute } from "@tanstack/react-router"

import { marketingPages } from "@/lib/marketing-pages"
import { seoMeta } from "@/lib/seo"
import { ResponsiveImage } from "@/routes/-components/site/ResponsiveImage"
import { SiteContentColumn } from "@/routes/-components/site/SiteContentColumn"
import { SiteSection } from "@/routes/-components/site/SiteSection"

export const Route = createFileRoute("/about/vision/")({
  head: () =>
    seoMeta({
      title: "Vision",
      description: marketingPages["/about/vision"].description,
      url: "https://entire.com/about/vision",
    }),
  component: VisionPage,
})

const introParagraphs = [
  "For decades, software development has had a certain flow to it. We as developers had an idea, opened our editors, changed a few files, checked if it runs, then pushed a commit and asked for a code review. Files, folders, diffs, pull requests, that was our little workshop. Line by line, human to human, out in the open, one commit at a time: software production moved at the speed of thought.",
  "But that order no longer exists. We live in an agent boom, and now massive volumes of code are generated faster than any human can reasonably understand. The role of the developer is shifting from writing code to conducting an orchestra of agents across AI-native IDEs and interfaces, spanning writing, reviewing, testing, securing, and deploying software.",
  "In a world where developers now prompt fleets of agents in multiple terminal windows at once; where specs are the primary driver of code generation; where agents can generate and compare hundreds of code variants; our manual system of software production – from issues, to repositories, to pull requests, to deployment – must foundationally evolve. Because the truth is: the software development lifecycle as we know it was never designed for the era of AI in the first place. And it is beginning to crack under the scale and complexity.",
  "Just like when automotive companies replaced the traditional craft-based production system with the moving assembly line, we must now reimagine the entire software development lifecycle for a world where machines are the primary producers of code. Together as the software ecosystem, it is time to create a new assembly line for the era of agents.",
]

const platformComponents = [
  {
    title: "Version Control for Agents",
    body: "We will enable agents to run at scale with a new git-compatible database layer that goes beyond storing source code to become an intelligent version control system of record, capturing intent, constraints, and agent context in a single place. This is a database where agents and humans can run at their own pace, without centralized rate limits, and where progress, intent, and outcomes are recorded as first-class data, not just code. The result is simple: agents move fast, humans retain governance, organizations have sovereignty, and every decision can be traced back to intent, not just code commits.",
  },
  {
    title: "Semantic Reasoning Layer",
    body: "While version control preserves change, it does not preserve meaning. And without shared context, agents cannot collaborate and build together. We will unlock agent-to-agent collaboration with a semantic reasoning layer directly tied to version control history, giving every agent persistent, shared, and pluggable memory. This preserves context so groups of humans and fleets of agents can reason, coordinate, and build together in parallel, without overwriting, colliding, or losing understanding.",
  },
  {
    title: "AI-Native Software Development Lifecycle",
    body: "When our agents are enabled to collaborate together to produce, secure and scale software, we will create an entirely new process of developer collaboration for humans. We will do this with an AI-native user interface that is fast and fresh, where developers can review, approve, and deploy hundreds of changes per day without ever feeling like they are the bottleneck. The result: we unlock human creativity by allowing anyone to bring any software idea to reality. By making software production far cheaper, we will ship projects that would never have been started in the old days.",
  },
]

function VisionPage() {
  return (
    <>
      <SiteSection innerClassName="px-6 py-10 md:px-20">
        <SiteContentColumn>
          <h1 className="text-site-title-lg font-headline text-default">Vision</h1>
          <div className="text-site-body flex flex-col gap-6 font-sans text-muted">
            {introParagraphs.map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}
          </div>
        </SiteContentColumn>
      </SiteSection>
      <SiteSection innerClassName="relative overflow-hidden bg-stone-950 px-6 py-12 md:px-20 md:py-10">
        <ResponsiveImage
          src="/images/vision-mission-bg.jpg"
          avifSrc="/images/vision-mission-bg.avif"
          alt=""
          width={2796}
          height={1572}
          loading="lazy"
          pictureClassName="pointer-events-none absolute inset-0 size-full"
          className="size-full object-cover"
        />
        <div className="relative mx-auto flex w-full max-w-180 flex-col items-center gap-6 text-center">
          <p className="text-site-body font-sans text-white">This is what Entire will do.</p>
          <p className="site-h1 text-white">
            <strong style={{ fontWeight: "inherit" }}>
              Entire is the world's next developer platform, where agents and humans can
              collaborate, learn and ship together.
            </strong>
          </p>
          <p className="text-site-body font-sans text-white">
            A platform that will be open, scalable, and independent, regardless of which model and
            agent you want to use.
          </p>
        </div>
      </SiteSection>
      <SiteSection innerClassName="px-6 py-10 md:px-20">
        <SiteContentColumn className="text-site-body font-sans text-muted">
          <p>
            <strong style={{ fontWeight: "inherit" }}>
              Our platform will be based on three core components:
            </strong>
          </p>
          {platformComponents.map((component) => (
            <section key={component.title} className="flex flex-col gap-4">
              <h2 className="text-site-title font-headline text-default">{component.title}</h2>
              <p>{component.body}</p>
            </section>
          ))}
          <p>
            And most importantly: our platform will be fast, joyful, and reliable, with a minimum of
            four nines of uptime, for every human and every agent on the planet.
          </p>
          <p className="text-site-final font-headline text-default">
            <strong style={{ fontWeight: "inherit" }}>
              The new era of software development begins here.
            </strong>
          </p>
        </SiteContentColumn>
      </SiteSection>
    </>
  )
}
